/**
 * AtBatExplorer - Interactive at-bat-by-at-bat pitch sequence viewer
 * Shows individual at-bats with pitch locations on a zone chart, pitch sequence
 * details (type, velocity, spin, break), and pitcher movement averages.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Card, Typography, Chip, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Pagination, useTheme, alpha, IconButton, Tooltip,
  Grid, Divider, useMediaQuery, ToggleButtonGroup, ToggleButton,
  Autocomplete, TextField,
} from '@mui/material';
import {
  SportsBaseball, FilterList, ArrowBack, ArrowForward,
} from '@mui/icons-material';
import { pitchSplitsApi } from '../services/apiService';
import { getPitchName, PITCH_COLORS as PITCH_COLORS_SHARED } from '../utils/pitchTypes';

// ── Mini spray chart for a single batted ball event ──
const MiniSprayChart = ({ zone, exitVelo, launchAngle, distance, result, inline = false }) => {
  const isHit = ['single', 'double', 'triple', 'home_run', 'home run'].some(h => (result || '').toLowerCase().includes(h));
  const isXBH = ['double', 'triple', 'home_run', 'home run'].some(h => (result || '').toLowerCase().includes(h));
  const dotColor = isXBH ? '#e74c3c' : isHit ? '#2ecc71' : '#aaa';
  // Compute field position from zone column → left/center/right
  const col = zone >= 1 && zone <= 9 ? (zone - 1) % 3 : 1;
  const angleRad = ((col - 1) * 35 * Math.PI) / 180; // -35, 0, or 35 degrees
  const maxR = 52;
  const norm = distance > 0 ? Math.min(distance / 420, 1) : 0.55;
  const r = norm * maxR;
  const cx = 60 + Math.sin(angleRad) * r;
  const cy = 80 - Math.cos(angleRad) * r;
  if (inline) {
    // Compact inline variant for at-bat list rows
    return (
      <svg viewBox="0 0 120 90" width={28} height={21} style={{ display: 'block', flexShrink: 0 }}>
        <path d="M60,80 L8,30 A72,72 0 0,1 112,30 Z" fill="rgba(76,175,80,0.10)" stroke="rgba(76,175,80,0.30)" strokeWidth="1" />
        <polygon points="60,62 70,55 60,48 50,55" fill="none" stroke="rgba(141,110,99,0.4)" strokeWidth="0.8" />
        <circle cx={cx} cy={cy} r={6} fill={dotColor} opacity={0.95} stroke="rgba(0,0,0,0.4)" strokeWidth={1} />
      </svg>
    );
  }
  return (
    <Box sx={{ textAlign: 'center' }}>
      <svg viewBox="0 0 120 90" width={110} height={83} style={{ display: 'block', margin: '0 auto' }}>
        <path d="M60,80 L8,30 A72,72 0 0,1 112,30 Z" fill="rgba(76,175,80,0.08)" stroke="rgba(76,175,80,0.25)" strokeWidth="0.8" />
        <polygon points="60,62 70,55 60,48 50,55" fill="none" stroke="rgba(141,110,99,0.35)" strokeWidth="0.5" />
        <polygon points="57,78 60,82 63,78 62,76 58,76" fill="#fff" stroke="#555" strokeWidth="0.5" />
        <line x1="60" y1="80" x2="34" y2="22" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" strokeDasharray="2,2" />
        <line x1="60" y1="80" x2="86" y2="22" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" strokeDasharray="2,2" />
        <circle cx={cx} cy={cy} r={5} fill={dotColor} opacity={0.9}
          stroke="rgba(0,0,0,0.3)" strokeWidth={0.8} />
      </svg>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
        {exitVelo ? `${Math.round(exitVelo)} mph` : ''}{launchAngle != null ? ` · ${Math.round(launchAngle)}°` : ''}{distance ? ` · ${Math.round(distance)} ft` : ''}
      </Typography>
    </Box>
  );
};

// ── Pitch type colors ──
const PITCH_COLORS = PITCH_COLORS_SHARED;

// ── Pitch result display ──
const RESULT_LABELS = {
  swstr: 'Swinging Strike',
  called: 'Called Strike',
  ball: 'Ball',
  foul: 'Foul',
  inplay: 'In Play',
  hit_by_pitch: 'Hit By Pitch',
  pitchout: 'Pitch Out',
  blocked: 'Blocked',
};

const RESULT_COLORS = {
  swstr: '#e74c3c',
  called: '#e67e22',
  ball: '#3498db',
  foul: '#f39c12',
  inplay: '#2ecc71',
};

// ── AB result categorization for color coding ──
function getResultCategory(result) {
  if (!result) return 'neutral';
  const r = result.toLowerCase();
  if (['single', 'double', 'triple', 'home run', 'home_run'].some(h => r.includes(h))) return 'hit';
  if (['walk', 'hit by pitch', 'hit_by_pitch', 'intentional walk'].some(h => r.includes(h))) return 'walk';
  if (['strikeout', 'strike out'].some(h => r.includes(h))) return 'strikeout';
  return 'out';
}

const RESULT_CATEGORY_COLORS = {
  hit: '#2ecc71',
  walk: '#3498db',
  strikeout: '#e74c3c',
  out: '#95a5a6',
  neutral: '#636e72',
};

// ═══════════════════════════════════════════════════════
// AtBatZoneChart - SVG zone chart for a single at-bat
// ═══════════════════════════════════════════════════════
const AtBatZoneChart = ({ pitches, size = 280 }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  if (!pitches || pitches.length === 0) return null;

  // Correct MLB zone: 17" plate + ball radius each side ≈ ±0.833 ft
  // Vertical: use per-batter szT/szB from Statcast when available
  const szTs = pitches.map(p => p.szT).filter(Boolean);
  const szBs = pitches.map(p => p.szB).filter(Boolean);
  const zoneLeft = -0.833, zoneRight = 0.833;
  const zoneTop = szTs.length > 0 ? szTs.reduce((a, b) => a + b, 0) / szTs.length : 3.5;
  const zoneBot = szBs.length > 0 ? szBs.reduce((a, b) => a + b, 0) / szBs.length : 1.5;
  const viewLeft = -2.0, viewRight = 2.0, viewBot = 0, viewTop = 4.5;

  const mapX = (x) => ((x - viewLeft) / (viewRight - viewLeft)) * size;
  const mapY = (y) => ((viewTop - y) / (viewTop - viewBot)) * (size * 1.15);

  const svgHeight = size * 1.15;

  return (
    <svg width={size} height={svgHeight} viewBox={`0 0 ${size} ${svgHeight}`}>
      <rect width={size} height={svgHeight} fill={isDark ? '#151a2e' : '#f5f7fa'} rx={8} />

      {/* Strike zone box */}
      <rect
        x={mapX(zoneLeft)} y={mapY(zoneTop)}
        width={mapX(zoneRight) - mapX(zoneLeft)}
        height={mapY(zoneBot) - mapY(zoneTop)}
        fill="none" stroke={isDark ? '#4a5080' : '#8892a8'} strokeWidth={2}
      />

      {/* Zone grid (3x3) */}
      {[1, 2].map(i => {
        const xFrac = zoneLeft + (i / 3) * (zoneRight - zoneLeft);
        const yFrac = zoneBot + (i / 3) * (zoneTop - zoneBot);
        return (
          <React.Fragment key={i}>
            <line x1={mapX(xFrac)} y1={mapY(zoneTop)} x2={mapX(xFrac)} y2={mapY(zoneBot)}
              stroke={isDark ? '#2a3050' : '#ccd3de'} strokeWidth={0.5} strokeDasharray="3,3" />
            <line x1={mapX(zoneLeft)} y1={mapY(yFrac)} x2={mapX(zoneRight)} y2={mapY(yFrac)}
              stroke={isDark ? '#2a3050' : '#ccd3de'} strokeWidth={0.5} strokeDasharray="3,3" />
          </React.Fragment>
        );
      })}

      {/* Home plate */}
      <polygon
        points={`${mapX(-0.7083)},${mapY(0.5)} ${mapX(-0.7083)},${mapY(0.25)} ${mapX(0)},${mapY(0)} ${mapX(0.7083)},${mapY(0.25)} ${mapX(0.7083)},${mapY(0.5)}`}
        fill={isDark ? '#2a3050' : '#e0e4ec'} stroke={isDark ? '#3a4060' : '#b0b8c8'} strokeWidth={1}
      />

      {/* Pitch locations with sequence numbers */}
      {pitches.map((p, i) => {
        const x = p.pX || 0;
        const y = p.pZ || 2.5;
        const color = PITCH_COLORS[p.pitchType] || '#888';
        const isLast = i === pitches.length - 1;
        const r = isLast ? 10 : 8;
        return (
          <g key={i}>
            <circle
              cx={mapX(x)} cy={mapY(y)} r={r}
              fill={color} opacity={0.85}
              stroke={isLast ? '#fff' : (isDark ? '#0a0e1a' : '#fff')}
              strokeWidth={isLast ? 2.5 : 1}
            />
            <text
              x={mapX(x)} y={mapY(y) + 1}
              textAnchor="middle" dominantBaseline="middle"
              fill="#fff" fontSize={r > 8 ? 11 : 9} fontWeight="bold"
              style={{ userSelect: 'none', pointerEvents: 'none' }}
            >
              {p.num}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ═══════════════════════════════════════════════════════
// PitchSequenceTable - Shows pitch-by-pitch data for an AB
// ═══════════════════════════════════════════════════════
const PitchSequenceTable = ({ pitches }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!pitches || pitches.length === 0) return null;

  return (
    <TableContainer sx={{ maxHeight: 300 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5, px: 1, whiteSpace: 'nowrap' }}>#</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5, px: 1, whiteSpace: 'nowrap' }}>Pitch</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5, px: 1, whiteSpace: 'nowrap' }} align="right">Velo</TableCell>
            {!isMobile && <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5, px: 1, whiteSpace: 'nowrap' }} align="right">Spin</TableCell>}
            {!isMobile && <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5, px: 1, whiteSpace: 'nowrap' }} align="right">hB</TableCell>}
            {!isMobile && <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5, px: 1, whiteSpace: 'nowrap' }} align="right">vB</TableCell>}
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5, px: 1, whiteSpace: 'nowrap' }}>Result</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pitches.map((p) => (
            <TableRow key={p.num} sx={{ '&:last-child td': { border: 0 } }}>
              <TableCell sx={{ py: 0.4, px: 1, fontSize: '0.75rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{
                    width: 18, height: 18, borderRadius: '50%',
                    bgcolor: PITCH_COLORS[p.pitchType] || '#888',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: '0.75rem', fontWeight: 700,
                  }}>
                    {p.num}
                  </Box>
                </Box>
              </TableCell>
              <TableCell sx={{ py: 0.4, px: 1, fontSize: '0.75rem', fontWeight: 600 }}>
                {getPitchName(p.pitchType)}
              </TableCell>
              <TableCell sx={{ py: 0.4, px: 1, fontSize: '0.75rem' }} align="right">
                {p.velocity > 0 ? `${p.velocity.toFixed(1)}` : '—'}
              </TableCell>
              {!isMobile && (
                <TableCell sx={{ py: 0.4, px: 1, fontSize: '0.75rem' }} align="right">
                  {p.spinRate > 0 ? p.spinRate : '—'}
                </TableCell>
              )}
              {!isMobile && (
                <TableCell sx={{ py: 0.4, px: 1, fontSize: '0.75rem' }} align="right">
                  {p.horizBreak != null ? `${p.horizBreak > 0 ? '+' : ''}${p.horizBreak}"` : '—'}
                </TableCell>
              )}
              {!isMobile && (
                <TableCell sx={{ py: 0.4, px: 1, fontSize: '0.75rem' }} align="right">
                  {p.vertBreak != null ? `${p.vertBreak > 0 ? '+' : ''}${p.vertBreak}"` : '—'}
                </TableCell>
              )}
              <TableCell sx={{ py: 0.4, px: 1 }}>
                <Chip
                  label={RESULT_LABELS[p.result] || p.result || '—'}
                  size="small"
                  sx={{
                    height: 20, fontSize: '0.75rem', fontWeight: 600,
                    bgcolor: alpha(RESULT_COLORS[p.result] || '#636e72', 0.15),
                    color: RESULT_COLORS[p.result] || '#636e72',
                    border: `1px solid ${alpha(RESULT_COLORS[p.result] || '#636e72', 0.3)}`,
                  }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ═══════════════════════════════════════════════════════
// MovementAveragesTable - Pitcher movement profile by pitch type
// ═══════════════════════════════════════════════════════
const MovementAveragesTable = ({ movementAverages }) => {
  if (!movementAverages || Object.keys(movementAverages).length === 0) return null;

  const sorted = Object.values(movementAverages).sort((a, b) => b.count - a.count);

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.75rem' }}>
          Pitch Movement Averages
        </Typography>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5, px: 1 }}>Pitch</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5, px: 1 }} align="right">Avg Horiz Break</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5, px: 1 }} align="right">Avg Vert Break</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', py: 0.5, px: 1 }} align="right">Pitches</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((pt) => (
              <TableRow key={pt.code} sx={{ '&:last-child td': { border: 0 } }}>
                <TableCell sx={{ py: 0.4, px: 1, fontSize: '0.75rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{
                      width: 10, height: 10, borderRadius: 1,
                      bgcolor: PITCH_COLORS[pt.code] || '#888',
                    }} />
                    <Typography variant="caption" fontWeight={600}>{pt.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 0.4, px: 1, fontSize: '0.75rem', fontFamily: 'monospace' }} align="right">
                  {pt.avgHorizBreak > 0 ? '+' : ''}{pt.avgHorizBreak}"
                </TableCell>
                <TableCell sx={{ py: 0.4, px: 1, fontSize: '0.75rem', fontFamily: 'monospace' }} align="right">
                  {pt.avgVertBreak > 0 ? '+' : ''}{pt.avgVertBreak}"
                </TableCell>
                <TableCell sx={{ py: 0.4, px: 1, fontSize: '0.75rem', color: 'text.secondary' }} align="right">
                  {pt.count}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════
// AtBatExplorer - Main component
// ═══════════════════════════════════════════════════════
const AtBatExplorer = ({ team, playerName, season, perspective = 'batter', playerType, gameTypeFilter = 'R', initialGameDate = '', initialVsPlayer = '', initialInning = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  // Two-way players can toggle perspective
  const isTwoWay = playerType === 'two-way';
  const [activePerspective, setActivePerspective] = useState(perspective);

  // Sync with prop changes
  useEffect(() => { setActivePerspective(perspective); }, [perspective]);

  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [page, setPage] = useState(1);

  // Filters
  const [opponentFilter, setOpponentFilter] = useState('');
  const [dateFilter, setDateFilter] = useState(initialGameDate);
  const [resultFilter, setResultFilter] = useState('');
  const [vsPlayerFilter, setVsPlayerFilter] = useState(initialVsPlayer);

  // Sync initial filters when props change (drill-down from Pitch Analysis)
  useEffect(() => { if (initialGameDate) setDateFilter(initialGameDate); }, [initialGameDate]);
  useEffect(() => { if (initialVsPlayer) setVsPlayerFilter(initialVsPlayer); }, [initialVsPlayer]);

  // Fetch at-bats from API
  const fetchAtBats = useCallback(async (pg = 1) => {
    if (!team || !playerName) return;
    setLoading(true);
    setError(null);
    try {
      const filters = {
        gameType: gameTypeFilter || 'R',
        page: pg,
        pageSize: 25,
      };
      if (opponentFilter) filters.opponent = opponentFilter;
      if (dateFilter) filters.gameDate = dateFilter;
      if (resultFilter) filters.resultFilter = resultFilter;
      if (vsPlayerFilter) filters.vsPlayer = vsPlayerFilter;

      const result = await pitchSplitsApi.getAtBats(activePerspective, team, playerName, season, filters);
      setData(result);
      // Auto-select at-bat matching drill-down inning (if provided)
      if (initialInning && result?.atBats?.length > 0) {
        const matchIdx = result.atBats.findIndex(ab => ab.inning === initialInning);
        setSelectedIdx(matchIdx >= 0 ? matchIdx : 0);
      } else {
        setSelectedIdx(0);
      }
    } catch (err) {
      console.error('AtBatExplorer fetch error:', err);
      setError('Failed to load at-bat data');
    } finally {
      setLoading(false);
    }
  }, [team, playerName, season, activePerspective, gameTypeFilter, opponentFilter, dateFilter, resultFilter, vsPlayerFilter]);

  // Initial fetch and re-fetch on filter change
  useEffect(() => {
    setPage(1);
    fetchAtBats(1);
  }, [fetchAtBats]);

  // Handle page change
  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    fetchAtBats(newPage);
  };

  // Reset filters
  const resetFilters = () => {
    setOpponentFilter('');
    setDateFilter('');
    setResultFilter('');
    setVsPlayerFilter('');
  };

  // Current at-bats and selected AB
  const atBats = data?.atBats || [];
  const selectedAB = atBats[selectedIdx] || null;
  const { movementAverages, filterOptions, resultCounts, total, totalPages } = data || {};

  // Result filter options from data
  const resultOptions = useMemo(() => {
    if (!resultCounts) return [];
    return Object.entries(resultCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([r, count]) => ({ value: r, label: `${r} (${count})` }));
  }, [resultCounts]);

  // Navigate between at-bats
  const goNext = () => setSelectedIdx(Math.min(selectedIdx + 1, atBats.length - 1));
  const goPrev = () => setSelectedIdx(Math.max(selectedIdx - 1, 0));

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Loading at-bats...</Typography>
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3, textAlign: 'center' }}>
        <SportsBaseball sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
        <Typography color="error">{error}</Typography>
      </Card>
    );
  }

  if (!data || total === 0) {
    return (
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3, textAlign: 'center' }}>
        <SportsBaseball sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No at-bat data available{opponentFilter || dateFilter || resultFilter || vsPlayerFilter ? ' for current filters' : ''}.
        </Typography>
      </Card>
    );
  }

  return (
    <Box>
      {/* ── Perspective Toggle (two-way players only) ── */}
      {isTwoWay && (
        <Card elevation={0} sx={{
          border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 1.5,
          p: 1, display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>View:</Typography>
          <ToggleButtonGroup
            value={activePerspective}
            exclusive
            onChange={(_, val) => { if (val) { setActivePerspective(val); setPage(1); setSelectedIdx(0); } }}
            size="small"
            sx={{ height: 28 }}
          >
            <ToggleButton value="pitcher" sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1.5, fontWeight: 600 }}>
              Pitching
            </ToggleButton>
            <ToggleButton value="batter" sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1.5, fontWeight: 600 }}>
              Batting
            </ToggleButton>
          </ToggleButtonGroup>
        </Card>
      )}

      {/* ── Filter Bar ── */}
      <Card elevation={0} sx={{
        border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2,
        p: 1.5, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center',
      }}>
        <FilterList sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="caption" fontWeight={700} sx={{ mr: 0.5 }}>Filters:</Typography>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel sx={{ fontSize: '0.75rem' }}>Opponent</InputLabel>
          <Select
            value={opponentFilter}
            onChange={(e) => setOpponentFilter(e.target.value)}
            label="Opponent"
            sx={{ fontSize: '0.75rem', height: 32 }}
          >
            <MenuItem value="" sx={{ fontSize: '0.75rem' }}>All</MenuItem>
            {(filterOptions?.opponents || []).map(opp => (
              <MenuItem key={opp} value={opp} sx={{ fontSize: '0.75rem' }}>{opp}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel sx={{ fontSize: '0.75rem' }}>Date</InputLabel>
          <Select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            label="Date"
            sx={{ fontSize: '0.75rem', height: 32 }}
          >
            <MenuItem value="" sx={{ fontSize: '0.75rem' }}>All Dates</MenuItem>
            {(filterOptions?.dates || []).map(d => (
              <MenuItem key={d} value={d} sx={{ fontSize: '0.75rem' }}>{d}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          size="small"
          freeSolo
          options={filterOptions?.opponentNames || []}
          value={vsPlayerFilter}
          onChange={(_, val) => setVsPlayerFilter(val || '')}
          onInputChange={(_, val, reason) => { if (reason === 'input') setVsPlayerFilter(val || ''); }}
          sx={{ minWidth: 160, '& .MuiInputBase-root': { height: 32, fontSize: '0.75rem' } }}
          renderInput={(params) => (
            <TextField {...params} label={activePerspective === 'pitcher' ? 'vs Batter' : 'vs Pitcher'}
              InputLabelProps={{ ...params.InputLabelProps, sx: { fontSize: '0.75rem' } }} />
          )}
          renderOption={(props, option) => (
            <li {...props} style={{ fontSize: '0.75rem' }}>{option}</li>
          )}
        />

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel sx={{ fontSize: '0.75rem' }}>Result</InputLabel>
          <Select
            value={resultFilter}
            onChange={(e) => setResultFilter(e.target.value)}
            label="Result"
            sx={{ fontSize: '0.75rem', height: 32 }}
          >
            <MenuItem value="" sx={{ fontSize: '0.75rem' }}>All Results</MenuItem>
            {resultOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.75rem' }}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {(opponentFilter || dateFilter || resultFilter || vsPlayerFilter) && (
          <Chip label="Clear" size="small" onClick={resetFilters} onDelete={resetFilters}
            sx={{ height: 24, fontSize: '0.75rem' }} />
        )}

        <Box sx={{ flex: 1 }} />

        <Typography variant="caption" color="text.secondary">
          {total} at-bat{total !== 1 ? 's' : ''}
        </Typography>
      </Card>

      <Grid container spacing={2}>
        {/* ── Left Panel: At-Bat List ── */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{
            border: '1px solid', borderColor: 'divider', borderRadius: 2,
            maxHeight: isMobile ? 300 : 580, overflow: 'auto',
          }}>
            <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 1,
              backdropFilter: 'blur(8px)',
            }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.75rem' }}>
                At-Bats
              </Typography>
              {loading && <CircularProgress size={14} />}
            </Box>

            {atBats.map((ab, idx) => {
              const cat = getResultCategory(ab.result);
              const isSelected = idx === selectedIdx;
              const lastP = ab.pitches?.[ab.pitches.length - 1];
              const hasBIP = !!(lastP?.exitVelo && lastP?.zone);
              return (
                <Box
                  key={idx}
                  onClick={() => setSelectedIdx(idx)}
                  sx={{
                    px: 1.5, py: 0.8,
                    cursor: 'pointer',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    borderLeft: isSelected ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                    transition: 'all 0.15s',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                      <Box sx={{
                        width: 8, height: 8, borderRadius: '50%',
                        bgcolor: RESULT_CATEGORY_COLORS[cat],
                        flexShrink: 0,
                      }} />
                      <Typography variant="caption" fontWeight={700} noWrap sx={{ fontSize: '0.75rem' }}>
                        {ab.result || 'Unknown'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {hasBIP && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center' }} title={`${Math.round(lastP.exitVelo)} mph${lastP.distance ? ` · ${Math.round(lastP.distance)} ft` : ''}`}>
                          <MiniSprayChart
                            zone={lastP.zone}
                            exitVelo={lastP.exitVelo}
                            launchAngle={lastP.launchAngle}
                            distance={lastP.distance}
                            result={ab.result}
                            inline
                          />
                        </Box>
                      )}
                      <Chip
                        label={`${ab.pitchCount}P`}
                        size="small"
                        sx={{ height: 18, fontSize: '0.75rem', fontWeight: 600, bgcolor: 'action.hover' }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                    {ab.opponentName && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                        vs {ab.opponentName}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      {ab.opponentName ? '·' : ''} {ab.homeAway === 'away' ? '@' : 'vs'} {ab.opponent}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      · {ab.gameDate}
                    </Typography>
                    {ab.inning && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        · Inn {ab.inning}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  size="small"
                  siblingCount={0}
                  sx={{ '& .MuiPaginationItem-root': { fontSize: '0.75rem', minWidth: 28, height: 28 } }}
                />
              </Box>
            )}
          </Card>
        </Grid>

        {/* ── Right Panel: Selected At-Bat Detail ── */}
        <Grid item xs={12} md={8}>
          {selectedAB ? (
            <Box>
              {/* AB Header */}
              <Card elevation={0} sx={{
                border: '1px solid', borderColor: 'divider', borderRadius: 2,
                p: 1.5, mb: 2,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={selectedAB.result || 'Unknown'}
                      size="small"
                      sx={{
                        fontWeight: 700, fontSize: '0.75rem',
                        bgcolor: alpha(RESULT_CATEGORY_COLORS[getResultCategory(selectedAB.result)], 0.15),
                        color: RESULT_CATEGORY_COLORS[getResultCategory(selectedAB.result)],
                        border: `1px solid ${alpha(RESULT_CATEGORY_COLORS[getResultCategory(selectedAB.result)], 0.4)}`,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {selectedAB.homeAway === 'away' ? '@' : 'vs'} {selectedAB.opponent} · {selectedAB.gameDate}
                      {selectedAB.inning ? ` · Inning ${selectedAB.inning}` : ''}
                      {selectedAB.opponentHand ? ` · vs ${selectedAB.opponentHand}H${activePerspective === 'pitcher' ? 'B' : 'P'}` : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton size="small" onClick={goPrev} disabled={selectedIdx === 0}>
                      <ArrowBack sx={{ fontSize: 16 }} />
                    </IconButton>
                    <Typography variant="caption" fontWeight={600}>
                      {selectedIdx + 1 + (page - 1) * 25} / {total}
                    </Typography>
                    <IconButton size="small" onClick={goNext} disabled={selectedIdx >= atBats.length - 1}>
                      <ArrowForward sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
              </Card>

              {/* Zone Chart + Pitch Sequence */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={5}>
                  <Card elevation={0} sx={{
                    border: '1px solid', borderColor: 'divider', borderRadius: 2,
                    p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center',
                  }}>
                    <Typography variant="caption" fontWeight={700} sx={{ mb: 0.5, fontSize: '0.75rem' }}>
                      Pitch Locations
                    </Typography>
                    <AtBatZoneChart pitches={selectedAB.pitches} size={isSmall ? 240 : 280} />

                    {/* Pitch type legend */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, justifyContent: 'center' }}>
                      {[...new Set(selectedAB.pitches.map(p => p.pitchType))].map(pt => (
                        <Typography key={pt} variant="caption" sx={{
                          display: 'flex', alignItems: 'center', gap: 0.3, fontSize: '0.75rem', color: 'text.secondary'
                        }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PITCH_COLORS[pt] || '#888' }} />
                          {getPitchName(pt)}
                        </Typography>
                      ))}
                    </Box>

                    {/* Mini spray chart for balls in play */}
                    {(() => {
                      const lastP = selectedAB.pitches[selectedAB.pitches.length - 1];
                      if (!lastP?.exitVelo || !lastP?.zone) return null;
                      return (
                        <Box sx={{ mt: 1.5, width: '100%', borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                          <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem', display: 'block', textAlign: 'center', mb: 0.5 }}>
                            Hit Location
                          </Typography>
                          <MiniSprayChart
                            zone={lastP.zone}
                            exitVelo={lastP.exitVelo}
                            launchAngle={lastP.launchAngle}
                            distance={lastP.distance}
                            result={selectedAB.result}
                          />

                        </Box>
                      );
                    })()}
                  </Card>
                </Grid>

                <Grid item xs={12} sm={7}>
                  <Card elevation={0} sx={{
                    border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden',
                  }}>
                    <Box sx={{ px: 1.5, py: 0.8, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                      <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>
                        Pitch Sequence ({selectedAB.pitchCount} pitch{selectedAB.pitchCount !== 1 ? 'es' : ''})
                      </Typography>
                    </Box>
                    <PitchSequenceTable pitches={selectedAB.pitches} />
                  </Card>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Card elevation={0} sx={{
              border: '1px solid', borderColor: 'divider', borderRadius: 2,
              p: 4, textAlign: 'center', height: '100%', display: 'flex',
              alignItems: 'center', justifyContent: 'center', minHeight: 200,
            }}>
              <Typography variant="body2" color="text.secondary">
                Select an at-bat from the list
              </Typography>
            </Card>
          )}

          {/* Movement Averages */}
          {movementAverages && Object.keys(movementAverages).length > 0 && (
            <Box sx={{ mt: 2 }}>
              <MovementAveragesTable movementAverages={movementAverages} />
            </Box>
          )}
        </Grid>
      </Grid>

    </Box>
  );
};

export default AtBatExplorer;
